import type { LabelType } from '@skop-app/lib-domain/types'

import {
  deleteMetadataKey,
  getMetadataValue,
  upsertMetadataValue,
} from '@/features/property/options/metadata'

import type { Metadata, PropertyView, SupportedKeys } from './property.type'

export const PROPERTY_VISIBLE_VIEWS = [
  'references',
  'inventory',
  'removal',
  'shop',
  'app',
] as const satisfies readonly PropertyView[]

export const PROPERTY_EDITABLE_VISIBLE_VIEWS = [
  'references',
  'inventory',
  'removal',
  'shop',
] as const satisfies readonly PropertyView[]

export const PROPERTY_VISIBLE_VIEWS_METADATA_KEY = 'visibleInViews'

type PropertyBooleanMetadataKey =
  | 'isRef'
  | 'isID'
  | 'isMulti'
  | 'hideInRef'
  | 'hideInInv'
  | 'hideInRem'
  | 'isPrivate'
  | 'isLocked'
  | 'autoWeightUnit'
  | 'localizedValues'

const BOOLEAN_METADATA_KEYS: PropertyBooleanMetadataKey[] = [
  'isRef',
  'isID',
  'isMulti',
  'hideInRef',
  'hideInInv',
  'hideInRem',
  'isPrivate',
  'isLocked',
  'autoWeightUnit',
  'localizedValues',
]

const parseBooleanMetadata = (
  metadata: Metadata[] | undefined,
  key: PropertyBooleanMetadataKey,
): boolean => getMetadataValue(metadata, key) === 'true'

const isPropertyView = (value: string): value is PropertyView =>
  PROPERTY_VISIBLE_VIEWS.includes(value as PropertyView)

export const normalizePropertyVisibleViews = (
  views: readonly string[] | undefined,
): PropertyView[] =>
  Array.from(
    new Set(
      (views ?? []).filter((view): view is PropertyView =>
        isPropertyView(view),
      ),
    ),
  )

const parseVisibleInViewsMetadata = (
  metadata: Metadata[] | undefined,
): PropertyView[] | undefined => {
  const raw = getMetadataValue(metadata, PROPERTY_VISIBLE_VIEWS_METADATA_KEY)
  if (!raw) return undefined

  try {
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return undefined

    return normalizePropertyVisibleViews(parsed)
  } catch {
    return undefined
  }
}

const deriveVisibleViewsFromLegacyMetadata = (
  metadata: Metadata[] | undefined,
): PropertyView[] =>
  PROPERTY_EDITABLE_VISIBLE_VIEWS.filter((view) => {
    switch (view) {
      case 'references':
        return !parseBooleanMetadata(metadata, 'hideInRef')
      case 'inventory':
        return !parseBooleanMetadata(metadata, 'hideInInv')
      case 'removal':
        return !parseBooleanMetadata(metadata, 'hideInRem')
      default:
        return false
    }
  })

const deriveLegacyVisibilityFlags = (
  metadata: Metadata[] | undefined,
): Pick<
  SupportedKeys,
  'hideInRef' | 'hideInInv' | 'hideInRem' | 'visibleInViews'
> => {
  const visibleInViews =
    parseVisibleInViewsMetadata(metadata) ??
    deriveVisibleViewsFromLegacyMetadata(metadata)

  return {
    visibleInViews,
    hideInRef: !visibleInViews.includes('references'),
    hideInInv: !visibleInViews.includes('inventory'),
    hideInRem: !visibleInViews.includes('removal'),
  }
}

const parseGroupMetadata = (
  metadata: Metadata[] | undefined,
): LabelType | undefined => {
  const raw = getMetadataValue(metadata, 'group')
  if (!raw || raw === '{}') return undefined

  try {
    return JSON.parse(raw) as LabelType
  } catch {
    return undefined
  }
}

const parseOrderMetadata = (
  metadata: Metadata[] | undefined,
): number | undefined => {
  const raw = getMetadataValue(metadata, 'order')
  if (raw == null || raw === '') return undefined
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : undefined
}

const upsertSystemMetadataValue = (
  metadata: Metadata[] | undefined,
  key: string,
  value: string | undefined,
): Metadata[] => {
  const next = upsertMetadataValue(metadata, key, value)
  const index = next.findIndex((entry) => entry.key === key)
  if (index < 0) return next

  next[index] = {
    ...next[index],
    type: next[index].type ?? 'system',
  }

  return next
}

export type ParsedPropertyFieldMetadata = Pick<
  SupportedKeys,
  | 'order'
  | 'isRef'
  | 'isID'
  | 'isMulti'
  | 'hideInRef'
  | 'hideInInv'
  | 'hideInRem'
  | 'visibleInViews'
  | 'isPrivate'
  | 'isLocked'
  | 'autoWeightUnit'
  | 'localizedValues'
  | 'suffix'
  | 'format'
  | 'group'
  | 'icon'
>

export const parsePropertyFieldMetadata = (
  metadata: Metadata[] | undefined,
): ParsedPropertyFieldMetadata => {
  const visibility = deriveLegacyVisibilityFlags(metadata)

  return {
    order: parseOrderMetadata(metadata),
    isRef: parseBooleanMetadata(metadata, 'isRef'),
    isID: parseBooleanMetadata(metadata, 'isID'),
    isMulti: parseBooleanMetadata(metadata, 'isMulti'),
    hideInRef: visibility.hideInRef,
    hideInInv: visibility.hideInInv,
    hideInRem: visibility.hideInRem,
    visibleInViews: visibility.visibleInViews,
    isPrivate: parseBooleanMetadata(metadata, 'isPrivate'),
    isLocked: parseBooleanMetadata(metadata, 'isLocked'),
    autoWeightUnit: parseBooleanMetadata(metadata, 'autoWeightUnit'),
    localizedValues: parseBooleanMetadata(metadata, 'localizedValues'),
    suffix: getMetadataValue(metadata, 'suffix'),
    format: getMetadataValue(metadata, 'format') as SupportedKeys['format'],
    group: parseGroupMetadata(metadata),
    icon: getMetadataValue(metadata, 'icon'),
  }
}

export type PropertyFieldMetadataInput = Omit<
  ParsedPropertyFieldMetadata,
  'order'
> & {
  order?: number
}

export const mergePropertyFieldMetadata = (
  metadata: Metadata[] | undefined,
  input: PropertyFieldMetadataInput,
): Metadata[] => {
  let next = Array.isArray(metadata) ? [...metadata] : []
  const visibleInViews = normalizePropertyVisibleViews(input.visibleInViews)

  if (input.order !== undefined) {
    next = upsertSystemMetadataValue(next, 'order', String(input.order))
  }

  next = upsertSystemMetadataValue(
    next,
    PROPERTY_VISIBLE_VIEWS_METADATA_KEY,
    JSON.stringify(visibleInViews),
  )

  for (const key of BOOLEAN_METADATA_KEYS) {
    const value = (() => {
      switch (key) {
        case 'hideInRef':
          return !visibleInViews.includes('references')
        case 'hideInInv':
          return !visibleInViews.includes('inventory')
        case 'hideInRem':
          return !visibleInViews.includes('removal')
        default:
          return input[key]
      }
    })()

    next = upsertSystemMetadataValue(next, key, value ? 'true' : 'false')
  }

  next =
    typeof input.suffix === 'string' && input.suffix.length > 0
      ? upsertSystemMetadataValue(next, 'suffix', input.suffix)
      : deleteMetadataKey(next, 'suffix')

  next =
    typeof input.format === 'string' && input.format.length > 0
      ? upsertSystemMetadataValue(next, 'format', input.format)
      : deleteMetadataKey(next, 'format')

  next = input.group
    ? upsertSystemMetadataValue(next, 'group', JSON.stringify(input.group))
    : deleteMetadataKey(next, 'group')

  next =
    typeof input.icon === 'string' && input.icon.length > 0
      ? upsertSystemMetadataValue(next, 'icon', input.icon)
      : deleteMetadataKey(next, 'icon')

  return next
}
